import React, { useState } from "react";
import type { Project, ProjectImage } from "../types";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { ImageLightbox } from "../../../components/ui/ImageLightbox";
import { ImageWithFallback } from "../../../components/ui/ImageWithFallback";
import { Section, SectionHeader } from "../../../components/ui/section";
import { cn } from "../../../lib/utils";
import {
  CONTRACTOR_SURFACE_RADIUS_LG_CLASS,
  CONTRACTOR_SURFACE_RADIUS_MD_CLASS,
} from "../layoutRadius";

type ProjectsSectionProps = {
  projects: Project[];
  projectImages?: ProjectImage[];
};

const FALLBACK_IMAGES = [
  "https://images.unsplash.com/photo-1755316388093-faff473ffdfb?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxob21lJTIwcmVub3ZhdGlvbiUyMGtpdGNoZW4lMjByZW1vZGVsfGVufDF8fHx8MTc3MTcxMjIzM3ww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
  "https://images.unsplash.com/photo-1768413292551-10011d6c354e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxiYXRocm9vbSUyMHJlbm92YXRpb24lMjBtb2Rlcm4lMjB0aWxlfGVufDF8fHx8MTc3MTcxMjIzM3ww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
  "https://images.unsplash.com/photo-1768173756660-45fd2bfe6be6?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxkZWNrJTIwcGF0aW8lMjBvdXRkb29yJTIwY29uc3RydWN0aW9ufGVufDF8fHx8MTc3MTcxMjIzNHww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
];

export const ProjectsSection: React.FC<ProjectsSectionProps> = ({
  projects,
  projectImages = [],
}) => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const hasUploadedImages = projectImages.length > 0;
  const hasTextProjects = projects.length > 0;

  const allImages = hasUploadedImages
    ? projectImages.map((img) => ({
        src: img.largeUrl || img.imageUrl,
        alt: img.title,
      }))
    : projects.map((p, i) => ({
        src:
          FALLBACK_IMAGES[i % FALLBACK_IMAGES.length] ??
          FALLBACK_IMAGES[0] ??
          "",
        alt: p.title,
      }));

  if (!hasUploadedImages && !hasTextProjects) {
    return null;
  }

  return (
    <>
      <Section id="projects" className="bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeader
            center
            className="mb-10"
            kicker="Our Work"
            title="Recent projects"
          />

          <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {projectImages.map((image, index) => (
              <li key={`image-${image.id}`}>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setLightboxIndex(index)}
                  className={cn(
                    "group relative aspect-4/3 h-auto w-full overflow-hidden p-0 hover:bg-transparent focus:ring-2 focus:ring-accent focus:ring-offset-2",
                    CONTRACTOR_SURFACE_RADIUS_LG_CLASS,
                  )}
                >
                  <ImageWithFallback
                    src={image.imageUrl}
                    alt={image.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </Button>
              </li>
            ))}

            {!hasUploadedImages &&
              projects.map((project, index) => (
                <li key={`project-${project.title}-${project.category}`}>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setLightboxIndex(index)}
                    className={cn(
                      "group relative aspect-4/3 h-auto w-full overflow-hidden p-0 hover:bg-transparent focus:ring-2 focus:ring-accent focus:ring-offset-2",
                      CONTRACTOR_SURFACE_RADIUS_LG_CLASS,
                    )}
                  >
                    <ImageWithFallback
                      src={FALLBACK_IMAGES[index % FALLBACK_IMAGES.length]}
                      alt={project.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-linear-to-t from-foreground/60 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    <div className="absolute bottom-0 left-0 right-0 translate-y-4 p-5 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                      <Badge className="mb-2" variant="default">
                        {project.category || "Project"}
                      </Badge>
                      <h3 className="font-display text-xl font-semibold text-primary-foreground">
                        {project.title}
                      </h3>
                    </div>
                  </Button>
                </li>
              ))}
          </ul>
        </div>
      </Section>

      {lightboxIndex !== null && (
        <ImageLightbox
          images={allImages}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
          imageClassName={CONTRACTOR_SURFACE_RADIUS_MD_CLASS}
        />
      )}
    </>
  );
};
